import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const commercialLawStructuredData = [
  {
    "@type": "WebPage",
    name: "特定商取引法に基づく表記",
    description: "ハヤブサワークスの特定商取引法に基づく表記。販売業者、所在地、販売価格、支払方法、返品についての特約事項を記載しています。",
    url: "https://raigeki-hayabusa.com/commercial-law",
    inLanguage: "ja",
    isPartOf: {
      "@type": "WebSite",
      name: "ハヤブサワークス",
      url: "https://raigeki-hayabusa.com",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ホーム",
          item: "https://raigeki-hayabusa.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "特定商取引法に基づく表記",
        },
      ],
    },
  },
];

export default function CommercialLaw() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100">
      <SEOHead
        title="特定商取引法に基づく表記｜ハヤブサワークス"
        description="ハヤブサワークスの特定商取引法に基づく表記。販売業者、所在地、販売価格、支払方法、返品についての特約事項を記載しています。"
        keywords="特定商取引法,通販,ハヤブサワークス"
        canonicalPath="/commercial-law"
        ogImage="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/cfb5afee631d88a7b12672494ae842fb.jpeg"
        structuredData={commercialLawStructuredData}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20abstract%20light%20background%20with%20soft%20warm%20toned%20paper%20texture%20in%20cream%20and%20light%20beige%2C%20gentle%20natural%20light%20from%20top%2C%20clean%20empty%20center%20area%2C%20subtle%20grain%20texture%2C%20artistic%20minimal%20composition%2C%20editorial%20style%20with%20quiet%20elegance&width=1600&height=600&seq=commercial-law-hero-2026&orientation=landscape"
            alt="特定商取引法に基づく表記"
            title="特定商取引法に基づく表記"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 40%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/35"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span
              className="text-background-50/80 text-xs tracking-[0.4em] mb-6"
              style={{ fontFamily: "var(--font-label)" }}
            >
              LEGAL
            </span>
            <h1
              className="text-background-50 text-2xl md:text-4xl leading-[1.2] max-w-4xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              特定商取引法に基づく表記
            </h1>
          </div>
        </section>

        {/* Content */}
        <section className="w-full px-6 md:px-10 max-w-[1600px] mx-auto py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-foreground-600 leading-relaxed mb-12">
              特定商取引に関する法律第11条に基づき、以下のとおり表示します。
            </p>

            {/* Table-style sections */}
            {[
              {
                label: "販売業者",
                value: "ハヤブサワークス",
              },
              {
                label: "運営統括責任者",
                value: "代表者に準ずる",
              },
              {
                label: "所在地",
                value: "島根県松江市",
              },
              {
                label: "電話番号",
                value: "必要に応じてお問い合わせフォームよりご請求いただければ、遅滞なく開示いたします。",
              },
              {
                label: "メールアドレス",
                value: "info@raigeki-hayabusa.com",
                isLink: true,
                href: "mailto:info@raigeki-hayabusa.com",
              },
              {
                label: "販売URL",
                value: "https://raigeki-hayabusa.stores.jp",
                isLink: true,
                href: "https://raigeki-hayabusa.stores.jp",
                isExternal: true,
              },
              {
                label: "販売価格について",
                value:
                  "販売価格は、表示された金額（表示価格・消費税込）とします。\n\nなお、商品代金以外に送料や各種手数料がかかる場合がございます。各商品ページや、お支払方法の選択ページ、注文内容のご確認ページをご確認ください。",
                multiline: true,
              },
              {
                label: "代金(対価)の支払時期と方法",
                value:
                  "■ 支払方法\n\nクレジットカード、コンビニ決済、後払い（コンビニ／銀行ATM）、atone 翌月払い（コンビニ／口座振替）、あと払い（ペイディ）、PayPal、銀行振込、キャリア決済、楽天ペイ、PayPay残高 がご利用いただけます。\n\n■ 支払時期\n\n・クレジットカード、PayPal、キャリア決済、楽天ペイ、PayPay残高\n商品ご注文時点でお支払いが確定します。\n\n・コンビニ決済、銀行振込\n商品注文時点から約3日間の期限内にお支払いください。お支払い期限を過ぎると、自動でキャンセルとなります。\n\n・後払い（コンビニ／銀行ATM）\n携帯番号とメールアドレスのみで利用できる安心・簡単な「後払い」です。商品注文完了後10日以内に、コンビニ・銀行ATMにてお支払いください。\n\n・atone 翌月払い（コンビニ／口座振替）\natoneを利用したお支払い方法です。1ヶ月分のご利用額をまとめて、商品注文完了の翌月10日までに、コンビニ・口座振替にてお支払いください。\n\n・あと払い（ペイディ）\nペイディを利用したお支払い方法です。1ヶ月分のご利用額をまとめて、翌月27日までにコンビニ・銀行振込にてお支払いください。\n\n・定期便のご注文について\n初回のお支払い日を起算日として、定められたサイクルでお支払いが発生します。次回の注文をキャンセルしたい場合は、次回お支払い日の前日までに解約手続きをしてください。",
                multiline: true,
              },
              {
                label: "返品についての特約事項",
                value:
                  "商品に欠陥がある場合を除き、返品には応じません。\nまた、お客さま都合による返品、交換の送料は、お客さまのご負担となります。予めご了承ください。",
                multiline: true,
              },
              {
                label: "役務または商品の引き渡し時期",
                value: "配送のご依頼を受けてから原則7日以内に発送いたします。",
              },
              {
                label: "その他",
                value:
                  "商品の発送前でしたら、ご変更を承ることが可能です。\nメールにてご連絡ください。\n（状況により、対応できかねる場合がございます。ご了承ください。）",
                multiline: true,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row gap-2 sm:gap-6 py-5 border-b border-background-200/60"
              >
                <dt className="sm:w-48 shrink-0 text-sm text-foreground-950 font-label tracking-wide">
                  {item.label}
                </dt>
                <dd className="text-sm text-foreground-600 leading-relaxed">
                  {item.isLink ? (
                    <a
                      href={item.href}
                      target={item.isExternal ? "_blank" : undefined}
                      rel={item.isExternal ? "noopener noreferrer" : undefined}
                      className="text-primary-500 hover:text-primary-600 transition cursor-pointer break-all"
                    >
                      {item.value}
                    </a>
                  ) : item.multiline ? (
                    item.value.split("\n").map((line, j) => (
                      <span key={j}>
                        {line}
                        {j < item.value.split("\n").length - 1 && <br />}
                      </span>
                    ))
                  ) : (
                    item.value
                  )}
                </dd>
              </div>
            ))}

            <div className="pt-12 text-center">
              <p className="text-xs text-foreground-600 mb-6">
                制定日：2026年7月14日
              </p>
              <a
                href="https://raigeki-hayabusa.stores.jp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-xs tracking-[0.25em] hover:bg-primary-600 transition cursor-pointer font-label"
              >
                公式オンラインショップ
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}